import React from 'react';
import { Check } from 'lucide-react';
import type { PlanType } from '@vidly/types';
import { PLAN_CONFIGS } from '@vidly/config';
import { useLanguage } from '../context/LanguageContext';

interface PricingTableProps {
  currentPlan?: PlanType;
  onSelectPlan: (plan: PlanType) => void;
  isLoading?: boolean;
}

export const PricingTable: React.FC<PricingTableProps> = ({
  currentPlan = 'free',
  onSelectPlan,
  isLoading = false
}) => {
  const { language, t } = useLanguage();
  const freeConfig = PLAN_CONFIGS.free;
  const premiumConfig = PLAN_CONFIGS.premium;

  const freeFeatures = language === 'en' ? [
    '1 video summary per day',
    'Up to 1 GB maximum upload',
    'Up to 20 minutes input length',
    'Standard quality frame-accurate cuts',
    '24-hour temporary cloud storage'
  ] : freeConfig.features;

  const premiumFeatures = language === 'en' ? [
    '15 video summaries per day',
    'Up to 5 GB maximum upload',
    'Up to 3 hours input length',
    'Priority high-speed queue',
    'No watermark & full HD export',
    '72-hour extended cloud storage'
  ] : premiumConfig.features;

  return (
    <div className="py-20 sm:py-32 lg:py-40 max-w-4xl mx-auto text-left space-y-12 sm:space-y-16">
      
      {/* Header */}
      <div className="text-center max-w-xl mx-auto space-y-4">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white">
          {t.pricing.title}
        </h2>
        <p className="text-base text-zinc-400 leading-relaxed font-normal pt-1">
          {t.pricing.subtitle}
        </p>
      </div>

      {/* Plan Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10 items-stretch">
        
        {/* FREE PLAN */}
        <div className="p-8 sm:p-10 rounded-3xl border border-zinc-800 bg-zinc-900/30 flex flex-col justify-between space-y-8">
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <span className="font-bold text-base text-white">{t.pricing.freeTitle}</span>
              <span className="text-xs text-zinc-400 px-2.5 py-0.5 rounded-full bg-zinc-900 border border-zinc-800 font-medium">
                {language === 'en' ? 'Get started' : 'Para empezar'}
              </span>
            </div>

            <div>
              <span className="text-4xl sm:text-5xl font-black text-white tracking-tight">
                {t.pricing.freePrice}
              </span>
              <span className="text-sm text-zinc-400 font-normal">
                {language === 'en' ? ' / forever' : ' / para siempre'}
              </span>
            </div>

            <p className="text-sm text-zinc-300 leading-relaxed font-normal">
              {language === 'en' ? 'Great for trying the tool on everyday videos.' : freeConfig.description}
            </p>

            <ul className="space-y-3.5 text-sm text-zinc-300 pt-4 border-t border-zinc-800/80">
              {freeFeatures.map((f, i) => (
                <li key={i} className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-zinc-500 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="pt-2">
            <button
              disabled={currentPlan === 'free'}
              onClick={() => onSelectPlan('free')}
              className={`w-full py-3.5 rounded-xl font-semibold text-sm transition-all ${
                currentPlan === 'free'
                  ? 'bg-zinc-900 text-zinc-500 border border-zinc-800 cursor-default'
                  : 'btn-secondary'
              }`}
            >
              {currentPlan === 'free'
                ? (language === 'en' ? 'Current plan' : 'Plan actual')
                : (language === 'en' ? 'Use free plan' : 'Usar plan gratuito')}
            </button>
          </div>
        </div>

        {/* PREMIUM PLAN */}
        <div className="p-8 sm:p-10 rounded-3xl border border-zinc-700 bg-zinc-900/50 flex flex-col justify-between space-y-8 relative">
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <span className="font-bold text-base text-white">{t.pricing.premiumTitle}</span>
              <span className="text-xs text-zinc-200 px-2.5 py-0.5 rounded-full bg-zinc-800 border border-zinc-600 font-medium">
                {language === 'en' ? 'Recommended' : 'Recomendado'}
              </span>
            </div>

            <div>
              <span className="text-4xl sm:text-5xl font-black text-white tracking-tight">
                {t.pricing.premiumPrice}
              </span>
              <span className="text-sm text-zinc-400 font-normal">
                {language === 'en' ? ' / month' : ' / mes'}
              </span>
            </div>

            <p className="text-sm text-zinc-300 leading-relaxed font-normal">
              {language === 'en' ? 'For creators, educators, and demanding professionals.' : premiumConfig.description}
            </p>

            <ul className="space-y-3.5 text-sm text-zinc-300 pt-4 border-t border-zinc-800/80">
              {premiumFeatures.map((f, i) => (
                <li key={i} className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="pt-2">
            <button
              disabled={isLoading}
              onClick={() => onSelectPlan('premium')}
              className={`w-full py-3.5 rounded-xl font-semibold text-sm transition-all ${
                currentPlan === 'premium'
                  ? 'bg-zinc-900 text-zinc-500 border border-zinc-800 cursor-default'
                  : 'btn-primary'
              }`}
            >
              {currentPlan === 'premium'
                ? (language === 'en' ? 'Current plan' : 'Plan actual')
                : (language === 'en' ? 'Upgrade to Premium' : 'Actualizar a Premium')}
            </button>
          </div>
        </div>

      </div>

    </div>
  );
};
